import React from 'react';
import { makeStyles } from '@mui/styles';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { Link, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import PaymentIcon from '@mui/icons-material/Payment';
import CodeIcon from '@mui/icons-material/Code';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import DomainIcon from '@mui/icons-material/Domain';
import RouterIcon from '@mui/icons-material/Router';
import SourceIcon from '@mui/icons-material/Source';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import CategoryIcon from '@mui/icons-material/Category';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { ListItemIcon } from '@mui/material';
import { useAuth } from '@redtrack/auth-core';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import ROUTES from '../../../constants/system/routes';
import { useTheme } from '../../../context/themeContext';
import { isRoleAllowed } from '../../../utils/roles';
import ListSubItem from './ListSubItem';

const useStyles = makeStyles(() => ({
  drawer: {
    width: (props) => props.drawerWidth,
  },
  drawerPaper: {
    width: (props) => props.drawerWidth,
    height: 'calc(100% - 64px) !important',
    marginTop: 64,
  },
  toggleButton: {
    top: '4px',
    right: '7px',
    alignSelf: 'self-end',
    border: (props) => `1px solid ${props.theme?.palette?.primary?.main} !important`,
  },
}));

function NavBar(props) {
  const {
    drawerWidth,
    toggleDrawer,
  } = props;
  const location = useLocation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const classes = useStyles({ drawerWidth, theme });
  const isSuperAdmin = isRoleAllowed(['ROLE_SUPERADMIN'], user?.roles || []);
  const links = [
    { path: ROUTES.DASHBOARD.INDEX.URL(), label: 'Dashboard', icon: <DashboardIcon /> },
    { path: ROUTES.USERS.INDEX.URL(), label: 'Users', icon: <GroupIcon /> },
    {
      path: ROUTES.TRANSACTIONS.INDEX.URL(),
      label: 'Transactions',
      icon: <PaymentIcon />,
      subitems: [
        { path: ROUTES.TRANSACTIONS.INDEX.URL(), label: 'Transactions' },
        { path: ROUTES.TRANSACTIONS.REPORT.URL(), label: 'Transactions Report' },
        { path: ROUTES.TRANSACTIONS.SETTINGS.URL(), label: 'Invoice Settings' },
        { path: ROUTES.TRANSACTIONS.SPECIAL_OFFERS.INDEX.URL(), label: 'Special Offers' },
      ],
    },
    { path: ROUTES.PROMO_CODES.INDEX.URL(), label: 'Promo codes', icon: <CodeIcon /> },
    { path: ROUTES.REFERRALS.INDEX.URL(), label: 'Referrals', icon: <ShuffleIcon /> },
    { path: ROUTES.DOMAINS.INDEX.URL(), label: 'Domains', icon: <DomainIcon /> },
    { path: ROUTES.NETWORK_PRESETS.INDEX.URL(), label: 'Network presets', icon: <RouterIcon /> },
    { path: ROUTES.SOURCE_PRESETS.INDEX.URL(), label: 'Source presets', icon: <SourceIcon /> },
    { path: ROUTES.PRESET_CATEGORIES.INDEX.URL(), label: 'Categories for Presets', icon: <CategoryIcon /> },
    { path: ROUTES.NEWS.INDEX.URL(), label: 'News', icon: <AnnouncementIcon /> },
    ...(isSuperAdmin ? [{
      path: ROUTES.TRAFFIC_FILTERS.INDEX.URL(),
      label: 'Traffic Filters',
      icon: <FilterAltIcon />,
    }] : []),
  ];

  return (
    <Drawer
      variant="permanent"
      className={classes.drawer}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <IconButton
        color="primary"
        className={classes.toggleButton}
        onClick={toggleDrawer}
      >
        {drawerWidth === 240 ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </IconButton>
      <List>
        {links.map((link) => {
          if (link?.subitems) {
            return (
              <ListSubItem
                key={link.path}
                link={link}
              />
            );
          }
          return (
            <ListItem key={link.path} disablePadding>
              <ListItemButton
                component={Link}
                to={link.path}
                selected={location.pathname === link.path}
              >
                <ListItemIcon>{link.icon}</ListItemIcon>
                <ListItemText primary={link.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
}

NavBar.propTypes = {
  drawerWidth: PropTypes.number.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
};

export default NavBar;
